import { useState } from 'react';
import { useI18n } from '@/i18n';
import { useTenant } from '@/contexts/TenantContext';
import {
  Badge,
  Button,
  Card,
  Input,
  Label,
  Spinner,
} from '@/components/ui';
import {
  useCACCatalog,
  useCACDecommissionArtifact,
  useCACDeleteArtifact,
  useCACMyArtifacts,
  useCACPublicationRequests,
  useCACRegisterArtifact,
  useCACRequestPublication,
  usePlatformEntities,
} from '@/features/cac';
import type { CACPublicationRequest, PlatformEntity } from '@/features/cac';
import { KindIcon } from '@/components/ui/kind-icon';
import { Globe, Package, Plus, Send, ShieldAlert, Upload } from 'lucide-react';
import { PageHeader } from '@vanguard-atlas/shared';

type TabId = 'register' | 'publish';

function publicErrorMessage(error: unknown): string {
  const defaultMessage = 'Operation failed. Please retry.';
  if (!error || typeof error !== 'object') return defaultMessage;
  const maybeAxios = error as { response?: { data?: { detail?: { code?: string; message?: string } } } };
  const detail = maybeAxios.response?.data?.detail;
  if (!detail?.code) return defaultMessage;
  switch (detail.code) {
    case 'CAC_LICENSE_REQUIRED': return 'A license template ref is required for commercial publications.';
    case 'CAC_CONTRACT_INVALID': return 'Publication request conflicts with an existing request.';
    default: return detail.message || defaultMessage;
  }
}

function levelBadge(level: 'free' | 'commercial') {
  return level === 'commercial'
    ? <Badge variant="default">Commercial</Badge>
    : <Badge variant="outline">Free</Badge>;
}

function statusBadge(entity: PlatformEntity) {
  if (entity.already_registered) return <Badge variant="secondary">Registered</Badge>;
  if (entity.status === 'active' || entity.status === 'ready') return <Badge variant="outline">{entity.status}</Badge>;
  return <Badge variant="destructive">{entity.status}</Badge>;
}

export function CACPublishPage() {
  const { t: _t } = useI18n();
  void _t;
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;
  const actorId = currentTenant?.slug ?? 'publisher';

  const [activeTab, setActiveTab] = useState<TabId>('register');
  const [selectedKind, setSelectedKind] = useState<string>('');

  // Register tab state
  const [selectedEntity, setSelectedEntity] = useState<PlatformEntity | null>(null);
  const [registerName, setRegisterName] = useState('');
  const [registerVersion, setRegisterVersion] = useState('');
  const [registerVisibility, setRegisterVisibility] = useState<'private' | 'tenant' | 'public' | 'marketplace'>('private');
  const [includeDeps, setIncludeDeps] = useState(true);

  // Publish tab state
  const [selectedArtifactId, setSelectedArtifactId] = useState('');
  const [selectedVersionId, setSelectedVersionId] = useState('');
  const [requestedLevel, setRequestedLevel] = useState<'free' | 'commercial'>('free');
  const [deliveryMode, setDeliveryMode] = useState<'installable' | 'use_only'>('installable');
  const [licenseRef, setLicenseRef] = useState('');

  // Queries
  const platformEntitiesQuery = usePlatformEntities(tenantId, selectedKind || undefined);
  const myArtifactsQuery = useCACMyArtifacts(tenantId);
  const catalogQuery = useCACCatalog(tenantId);
  const publicationRequestsQuery = useCACPublicationRequests(tenantId);
  const requestPublication = useCACRequestPublication(tenantId);
  const registerArtifact = useCACRegisterArtifact(tenantId);
  const decommissionArtifact = useCACDecommissionArtifact();
  const deleteArtifact = useCACDeleteArtifact();

  const ownArtifacts: Array<{ artifact_id: string; artifact_slug: string; kind: string; version_id: string | null; version: string | null }> = (myArtifactsQuery.data ?? []);

  const selectedArtifact = ownArtifacts.find(
    (a) => a.artifact_id === selectedArtifactId,
  );

  // ── Register handlers ──

  const [registerError, setRegisterError] = useState<string | null>(null);

  const handleRegister = async () => {
    if (!selectedEntity) return;
    setRegisterError(null);
    try {
      const result = await registerArtifact.mutateAsync({
        entity_type: selectedEntity.entity_type,
        entity_id: selectedEntity.entity_id,
        name: registerName || undefined,
        version: registerVersion || undefined,
        visibility: registerVisibility,
        include_dependencies: includeDeps,
      });
      setSelectedEntity(null);
      setRegisterName('');
      setRegisterVersion('');
      // Auto-fill Publish tab with the newly registered artifact
      setSelectedArtifactId(result.artifact_id);
      setSelectedVersionId(result.version_id);
      setActiveTab('publish');
    } catch {
      // ISS-289: register failures (e.g. edge/WAF 403 between DR and CP)
      // must surface a readable message — never internal details (SEC-005).
      setRegisterError(
        'Unable to register the artifact. Please try again or contact support.',
      );
    }
  };

  // ── Publish handlers ──

  const handleSubmit = async () => {
    if (!selectedArtifactId || !selectedVersionId) return;
    await requestPublication.mutateAsync({
      artifact_id: selectedArtifactId,
      version_id: selectedVersionId,
      requested_level: requestedLevel,
      delivery_mode: deliveryMode,
      requestedById: actorId,
      license_template_ref: requestedLevel === 'commercial' && licenseRef ? licenseRef : undefined,
    });
    setSelectedArtifactId('');
    setSelectedVersionId('');
    setRequestedLevel('free');
    setDeliveryMode('installable');
    setLicenseRef('');
  };

  const registeredCount = (platformEntitiesQuery.data ?? []).filter(e => e.already_registered).length;

  return (
    <div className="space-y-6" data-testid="cac-publish-page">
      <PageHeader
        icon={Upload}
        title="My Publications"
        subtitle="Register platform entities as CAC artifacts and publish them to the marketplace."
        testId="marketplace-publish-header"
      />

      {!tenantId ? (
        <Card className="p-6 border-amber-200 dark:border-amber-800">
          <div className="flex items-center gap-2 text-amber-700 dark:text-amber-300">
            <ShieldAlert className="h-4 w-4" />
            Select a tenant to register and publish artifacts.
          </div>
        </Card>
      ) : null}

      {/* Tabs */}
      <div className="flex gap-1 border-b border-border pb-px" role="tablist">
        <button
          role="tab"
          aria-selected={activeTab === 'register'}
          onClick={() => setActiveTab('register')}
          className={`px-4 py-2 text-sm font-medium rounded-t-md transition-colors ${
            activeTab === 'register'
              ? 'bg-background border border-border border-b-background text-foreground -mb-px'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          <Package className="h-4 w-4 inline mr-1.5" />
          Register
        </button>
        <button
          role="tab"
          aria-selected={activeTab === 'publish'}
          onClick={() => setActiveTab('publish')}
          className={`px-4 py-2 text-sm font-medium rounded-t-md transition-colors ${
            activeTab === 'publish'
              ? 'bg-background border border-border border-b-background text-foreground -mb-px'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          <Globe className="h-4 w-4 inline mr-1.5" />
          Publish ({ownArtifacts.length})
        </button>
      </div>

      {/* ─── REGISTER TAB ─── */}
      {activeTab === 'register' && (
        <div className="space-y-4">
          {/* Kind filter */}
          <div className="flex gap-2 flex-wrap">
            {['', 'agent', 'hive', 'collection', 'knowledge_base'].map((kind) => (
              <button
                key={kind}
                onClick={() => { setSelectedKind(kind); setSelectedEntity(null); }}
                className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${
                  selectedKind === kind
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-background text-muted-foreground border-border hover:border-primary'
                }`}
              >
                {kind ? <><KindIcon kind={kind} size={14} className="inline" /> {kind}s</> : 'All'}
              </button>
            ))}
          </div>

          {/* Entity list */}
          {platformEntitiesQuery.isLoading ? (
            <div className="flex items-center gap-2 py-4">
              <Spinner />
              <span className="text-sm text-muted-foreground">Loading platform entities…</span>
            </div>
          ) : null}

          {platformEntitiesQuery.data && platformEntitiesQuery.data.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4">
              No platform entities found for this tenant.
            </p>
          ) : null}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {(platformEntitiesQuery.data ?? []).map((entity) => (
              <Card
                key={`${entity.entity_type}-${entity.entity_id}`}
                className={`p-3 cursor-pointer transition-colors hover:border-primary hover:shadow-sm ${
                  selectedEntity?.entity_id === entity.entity_id ? 'border-primary ring-1 ring-primary' : ''
                }`}
                onClick={() => {
                  setSelectedEntity(entity);
                  setRegisterName(entity.name);
                }}
              >
                <div className="flex items-start gap-2">
                  <KindIcon kind={entity.entity_type} size={22} className="text-muted-foreground shrink-0 mt-0.5" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm truncate">{entity.name}</span>
                      {statusBadge(entity)}
                    </div>
                    {entity.description ? (
                      <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{entity.description}</p>
                    ) : null}
                    <div className="flex gap-3 mt-1.5 text-xs text-muted-foreground">
                      <span className="font-mono">{entity.entity_type}</span>
                      <span>v{entity.version}</span>
                      {entity.dependency_count > 0 ? (
                        <span>{entity.dependency_count} deps</span>
                      ) : null}
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>

          {/* Registration form */}
          {selectedEntity && !selectedEntity.already_registered ? (
            <Card className="p-4 space-y-4 border-primary/50">
              <h3 className="font-semibold flex items-center gap-2">
                <Plus className="h-4 w-4 text-primary" />
                Register &ldquo;{selectedEntity.name}&rdquo; in CAC
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label htmlFor="reg-name">Name (optional override)</Label>
                  <Input
                    id="reg-name"
                    value={registerName}
                    onChange={(e) => setRegisterName(e.target.value)}
                    placeholder={selectedEntity.name}
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="reg-version">Version (optional)</Label>
                  <Input
                    id="reg-version"
                    value={registerVersion}
                    onChange={(e) => setRegisterVersion(e.target.value)}
                    placeholder="1.0.0"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="reg-visibility">Initial visibility</Label>
                  <select
                    id="reg-visibility"
                    value={registerVisibility}
                    onChange={(e) => setRegisterVisibility(e.target.value as typeof registerVisibility)}
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  >
                    <option value="private">Private</option>
                    <option value="tenant">Tenant</option>
                    <option value="public">Public</option>
                    <option value="marketplace">Marketplace</option>
                  </select>
                </div>
              </div>

              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={includeDeps}
                  onChange={(e) => setIncludeDeps(e.target.checked)}
                  className="rounded border-input"
                />
                Include dependencies (collections, documents, member agents)
              </label>

              {registerArtifact.isError ? (
                <p className="text-sm text-destructive">
                  {publicErrorMessage(registerArtifact.error)}
                </p>
              ) : null}

              {registerArtifact.isSuccess ? (
                <div className="text-sm text-green-700 dark:text-green-400 space-y-1">
                  <p>✅ Registered successfully!</p>
                  <p className="font-mono text-xs">
                    {registerArtifact.data.artifact_id} / {registerArtifact.data.version_id}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    OCI ref: {registerArtifact.data.oci_ref}
                  </p>
                </div>
              ) : null}

              {registerError ? (
                <div className="mb-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-300">
                  {registerError}
                </div>
              ) : null}

              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setSelectedEntity(null)}>
                  Cancel
                </Button>
                <Button
                  disabled={registerArtifact.isPending}
                  onClick={() => void handleRegister()}
                >
                  {registerArtifact.isPending ? 'Registering…' : 'Register in CAC'}
                </Button>
              </div>
            </Card>
          ) : null}

          {selectedEntity?.already_registered ? (
            <Card className="p-4 border-amber-200 dark:border-amber-800">
              <p className="text-sm text-amber-700 dark:text-amber-300">
                This entity is already registered as a CAC artifact. Switch to the &ldquo;Publish&rdquo; tab to submit a publication request.
              </p>
            </Card>
          ) : null}

          {/* Summary */}
          <p className="text-xs text-muted-foreground">
            {platformEntitiesQuery.data?.length ?? 0} entities found
            {registeredCount > 0 ? `, ${registeredCount} already registered` : ''}
          </p>
        </div>
      )}

      {/* ─── PUBLISH TAB ─── */}
      {activeTab === 'publish' && (
        <div className="space-y-4">
          <Card className="p-4 space-y-4" data-testid="cac-publish-form">
            <h2 className="font-semibold">New publication request</h2>

            {catalogQuery.isLoading ? (
              <div className="flex items-center gap-2 py-4">
                <Spinner />
                <span className="text-sm text-muted-foreground">Loading artifacts…</span>
              </div>
            ) : null}

            {!catalogQuery.isLoading && ownArtifacts.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No CAC artifacts available. Register a platform entity first in the &ldquo;Register&rdquo; tab.
              </p>
            ) : null}

            {ownArtifacts.length > 0 ? (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {ownArtifacts.map((a) => (
                    <Card
                      key={`${a.artifact_id}-${a.version_id}`}
                      className={`p-3 cursor-pointer transition-colors hover:shadow-sm ${
                        selectedArtifactId === a.artifact_id ? 'border-primary ring-1 ring-primary' : 'hover:border-primary'
                      }`}
                      onClick={() => {
                        setSelectedArtifactId(a.artifact_id);
                        setSelectedVersionId(a.version_id ?? '');
                      }}
                    >
                      <div className="flex items-start gap-2">
                        <KindIcon kind={a.kind} size={20} className="text-muted-foreground shrink-0 mt-0.5" />
                        <div className="min-w-0 flex-1">
                          <span className="font-medium text-sm truncate block">{a.artifact_slug}</span>
                          <div className="flex items-center gap-1.5 mt-1">
                            <Badge variant="outline" className="text-xs">{a.kind}</Badge>
                            {a.version ? (
                              <Badge variant="outline" className="text-xs font-mono">v{a.version}</Badge>
                            ) : null}
                          </div>
                          {/* REQ-CAC-021: owner lifecycle actions */}
                          <div
                            className="mt-2 flex items-center gap-1.5"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={decommissionArtifact.isPending || deleteArtifact.isPending}
                              onClick={() => {
                                if (window.confirm(`Decommission artifact "${a.artifact_slug}"? Its active offers will be revoked.`)) {
                                  void decommissionArtifact.mutateAsync(a.artifact_id);
                                }
                              }}
                            >
                              Decommission
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-destructive"
                              disabled={decommissionArtifact.isPending || deleteArtifact.isPending}
                              onClick={() => {
                                if (window.confirm(`Delete draft artifact "${a.artifact_slug}"?`)) {
                                  void deleteArtifact.mutateAsync(a.artifact_id);
                                }
                              }}
                            >
                              Delete
                            </Button>
                          </div>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                <div className="space-y-1">
                  <Label htmlFor="artifact-select">Artifact</Label>
                  <select
                    id="artifact-select"
                    value={selectedArtifactId}
                    onChange={(e) => {
                      const artifact = ownArtifacts.find(a => a.artifact_id === e.target.value);
                      setSelectedArtifactId(e.target.value);
                      setSelectedVersionId(artifact?.version_id ?? '');
                    }}
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  >
                    <option value="">Select an artifact…</option>
                    {ownArtifacts.map((a) => (
                      <option key={`${a.artifact_id}-${a.version_id}`} value={a.artifact_id}>
                        {a.artifact_slug} ({a.kind}){a.version ? ` v${a.version}` : ''}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-1">
                  <Label htmlFor="delivery-select">Delivery mode</Label>
                  <select
                    id="delivery-select"
                    value={deliveryMode}
                    onChange={(e) => setDeliveryMode(e.target.value as 'installable' | 'use_only')}
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  >
                    <option value="installable">Installable — consumers can inspect authorized internals</option>
                    <option value="use_only">Use only — consumers get execution rights without internal access (black box)</option>
                  </select>
                </div>

                <div className="space-y-1">
                  <Label htmlFor="level-select">Publication level</Label>
                  <select
                    id="level-select"
                    value={requestedLevel}
                    onChange={(e) => setRequestedLevel(e.target.value as 'free' | 'commercial')}
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  >
                    <option value="free">Free</option>
                    <option value="commercial">Commercial</option>
                  </select>
                </div>

                {requestedLevel === 'commercial' ? (
                  <div className="space-y-1">
                    <Label htmlFor="license-ref">License template ref</Label>
                    <Input
                      id="license-ref"
                      placeholder="lic-template-id"
                      value={licenseRef}
                      onChange={(e) => setLicenseRef(e.target.value)}
                    />
                  </div>
                ) : null}
              </div>
              </>
            ) : null}

            {selectedArtifact ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <KindIcon kind={selectedArtifact.kind} size={16} />
                <Badge variant="outline">{selectedArtifact.kind}</Badge>
                {selectedArtifact.version ? (
                  <Badge variant="outline" className="font-mono">v{selectedArtifact.version}</Badge>
                ) : null}
                <span className="font-mono text-xs">{selectedArtifact.artifact_id}</span>
              </div>
            ) : null}

            {requestPublication.isError ? (
              <p className="text-sm text-destructive" data-testid="cac-publish-error">
                {publicErrorMessage(requestPublication.error)}
              </p>
            ) : null}

            {requestPublication.isSuccess ? (
              <p className="text-sm text-green-700 dark:text-green-400" data-testid="cac-publish-success">
                Publication request submitted successfully.
              </p>
            ) : null}

            <div className="flex justify-end">
              <Button
                disabled={!tenantId || !selectedArtifactId || !selectedVersionId || requestPublication.isPending}
                onClick={() => void handleSubmit()}
              >
                <Send className="h-4 w-4 mr-1" />
                Submit request
              </Button>
            </div>
          </Card>

          {/* Existing requests */}
          {publicationRequestsQuery.data && publicationRequestsQuery.data.length > 0 ? (
            <Card className="p-4" data-testid="cac-publish-requests-list">
              <h3 className="font-semibold mb-3">Existing requests</h3>
              <div className="space-y-2">
                {publicationRequestsQuery.data.map((req: CACPublicationRequest) => (
                  <div key={req.id} className="text-sm flex items-center justify-between gap-2 border-b border-border/40 pb-2 last:border-0 last:pb-0">
                    <div className="min-w-0">
                      <p className="font-mono text-xs truncate">{req.id}</p>
                      <p className="text-muted-foreground truncate">artifact {req.artifact_id}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {levelBadge(req.requested_level)}
                      <Badge
                        variant={
                          req.status === 'published' ? 'default'
                          : req.status === 'failed' || req.status === 'rejected' ? 'destructive'
                          : 'secondary'
                        }
                      >
                        {req.status}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          ) : null}
        </div>
      )}
    </div>
  );
}
